import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { KibField, KibSelect } from '@chewy/kib-fields-new-react'
import classNames from 'classnames'
import * as R from 'ramda'
import type { Field } from '@pbt/pbt-ui-components'

import FeatureToggle from '~/constants/featureToggle'
import {
  useGetCompoundingReasonTypeById,
  useGetIsCompoundingReasonDetailsRequired,
} from '~/store/hooks/prescription'
import {
  getFeatureToggle,
  getPrescriptionCompoundingReason,
} from '~/store/reducers/constants'
import {
  DOSAGE_FORM_ID,
  DOSAGE_FORM_V2_ID,
} from '~/types/entities/prescriptions'

import styles from './SwapProductCompoundingReason.module.scss'

interface SwapProductCompoundingReasonProps {
  compoundingReasonField: Field
  compoundingReasonIdField: Field
}

const SwapProductCompoundingReason = ({
  compoundingReasonField,
  compoundingReasonIdField,
}: SwapProductCompoundingReasonProps) => {
  const { t } = useTranslation('Common')

  const prescriptionCompoundingReason = useSelector(
    getPrescriptionCompoundingReason,
  )
  const isRhapUpdateReasonForCompoundingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.RHAP_UPDATE_REASON_FOR_COMPOUNDING),
  )

  const { isAllergiesIntolerances } = useGetCompoundingReasonTypeById()(
    compoundingReasonIdField.value,
  )
  const isCompoundingReasonDetailsRequired =
    useGetIsCompoundingReasonDetailsRequired()(compoundingReasonIdField.value)

  // Filter out the incorrect compounding reason, and ensure the other is at the top of the list
  const excludeId = isRhapUpdateReasonForCompoundingEnabled
    ? DOSAGE_FORM_ID
    : DOSAGE_FORM_V2_ID
  const isPriority = R.pipe(
    R.prop('id'),
    R.includes(R.__, [DOSAGE_FORM_V2_ID, DOSAGE_FORM_ID]),
  )

  const sortedCompoundingReasons = R.pipe(
    R.reject(R.propEq('id', excludeId)),
    R.partition(isPriority),
    R.unnest,
  )(prescriptionCompoundingReason)

  return (
    <div className={styles.compoundingContainer}>
      <KibSelect
        required
        className={classNames({
          [styles.placeholderColor]: !compoundingReasonIdField.value,
        })}
        invalid={compoundingReasonIdField.errors.length > 0}
        label={t('Common:REASON_FOR_COMPOUNDING.REASON_FOR_COMPOUNDING')}
        messagesContent={compoundingReasonIdField.message}
        value={compoundingReasonIdField.value || ''}
        onChange={(e) => {
          compoundingReasonIdField.setValue(e.target.value)
        }}
      >
        <option disabled hidden value="">
          {t('Common:REASON_FOR_COMPOUNDING.REASON_FOR_COMPOUNDING')}
        </option>
        {sortedCompoundingReasons.map((reason) => (
          <option key={reason.id} value={reason.id}>
            {reason.name}
          </option>
        ))}
      </KibSelect>
      {isCompoundingReasonDetailsRequired && (
        <KibField
          required
          className={styles.compoundingDetailsField}
          invalid={compoundingReasonField.errors.length > 0}
          label={
            isAllergiesIntolerances
              ? t(
                  'Common:REASON_FOR_COMPOUNDING.INPUT_LABEL_ALLERGIES_INTOLERANCES',
                )
              : t('Common:REASON_FOR_COMPOUNDING.INPUT_LABEL_OTHER')
          }
          messagesContent={compoundingReasonField.message}
          type="text"
          value={compoundingReasonField.value || ''}
          onChange={(e) => {
            compoundingReasonField.setValue(e.target.value)
          }}
        />
      )}
    </div>
  )
}

export default SwapProductCompoundingReason
